import { CalendarFilled } from '@ant-design/icons'
import { Checkbox, DatePicker, Input, Select, Typography, message } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { getTeamGroups, getTeamUsers } from '../../../api/aops.api'
import {
  createWatchdogTask,
  deleteWatchdogTask,
  getWatchdogTaskDetail,
  updateWatchdogTask,
} from '../../../api/watchdog.api'
import '../../../components/apos/modal/aopsCommonModal.scss'
import AopsCommonModalLayout from '../../../components/apos/modal/AopsCommonModalLayout'
import { menuSelectedTeam } from '../../../store/aops'
const { Text } = Typography

const StandaloneWatchdogTaskPopup = ({
  open,
  onClose,
  watchdogId,
  taskId,
  task,
  onSuccess,
}) => {
  const [selecteTeamByMenu] = useRecoilState(menuSelectedTeam)
  const [teamGroupId, setTeamGroupId] = useState(null)
  const [teamUsers, setTeamUsers] = useState([])
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    endDate: null,
    hour: '09',
    minute: '30',
    selectedManagers: [],
    subTasks: [],
    memo: '',
  })

  const [tempSubTask, setTempSubTask] = useState('')
  const [isInputActive, setIsInputActive] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const isEditMode = !!taskId

  const fetchTeamGroups = async () => {
    const res = await getTeamGroups()
    const selectedTeamGroup = res.find(
      (item) => item.name === selecteTeamByMenu,
    )
    setTeamGroupId(selectedTeamGroup?.id)
  }

  useEffect(() => {
    if (selecteTeamByMenu) {
      fetchTeamGroups()
    }
  }, [selecteTeamByMenu])

  const fetchTeamUsers = async () => {
    if (!teamGroupId) return
    try {
      const res = await getTeamUsers(teamGroupId)
      setTeamUsers(res || [])
    } catch (error) {
      console.log('error::', error)
      setTeamUsers([])
    }
  }

  useEffect(() => {
    if (teamGroupId) {
      fetchTeamUsers()
    }
  }, [teamGroupId])

  // Task 상세 조회
  const fetchTaskDetail = async () => {
    if (!taskId) return

    setLoading(true)
    try {
      const taskDetail = await getWatchdogTaskDetail(taskId)
      const endDateTime = dayjs(taskDetail.endDateTime)
      const subTasks =
        taskDetail.subTasks?.map((st) => ({
          id: st.id,
          title: st.title,
          isDone: st.isDone || false,
        })) || []

      setFormData({
        title: taskDetail.title || '',
        endDate: endDateTime,
        hour: String(endDateTime.hour()).padStart(2, '0'),
        minute: String(endDateTime.minute()).padStart(2, '0'),
        selectedManagers: taskDetail.managers?.map((m) => m.userId) || [],
        subTasks: subTasks,
        memo: taskDetail.memo || '',
      })
    } catch (error) {
      console.log('error::', error)
      message.error('Task 정보를 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) {
      setFormData({
        title: '',
        endDate: null,
        hour: '09',
        minute: '30',
        selectedManagers: [],
        subTasks: [],
        memo: '',
      })
      setTempSubTask('')
      setIsInputActive(false)
      setEditingId(null)
    } else if (open && taskId) {
      // Edit mode: Task 상세 조회
      fetchTaskDetail()
    }
  }, [open, taskId])

  const hours = Array.from({ length: 24 }, (_, idx) =>
    String(idx).padStart(2, '0'),
  )
  const minutes = Array.from({ length: 6 }, (_, idx) =>
    String(idx * 10).padStart(2, '0'),
  )

  const handleInputSubmit = () => {
    if (!tempSubTask.trim()) {
      message.warning('내용을 입력해주세요.')
      return
    }
    setFormData((prev) => ({
      ...prev,
      subTasks: [
        ...prev.subTasks,
        {
          id: `new-${Date.now()}`,
          title: tempSubTask,
          isDone: false,
        },
      ],
    }))
    setTempSubTask('')
    setIsInputActive(false)
  }

  const removeSubTask = (targetId) => {
    setFormData((prev) => ({
      ...prev,
      subTasks: prev.subTasks.filter((item) => item.id !== targetId),
    }))
  }

  const toggleCheckItem = (id) => {
    // 로컬 상태만 변경 (CSS로만 표시)
    setFormData((prev) => ({
      ...prev,
      subTasks: prev.subTasks.map((item) =>
        item.id === id ? { ...item, isDone: !item.isDone } : item,
      ),
    }))
  }

  const handleTitleChange = (id, newTitle) => {
    setFormData((prev) => ({
      ...prev,
      subTasks: prev.subTasks.map((item) =>
        item.id === id ? { ...item, title: newTitle } : item,
      ),
    }))
  }

  const toggleManager = (userId) => {
    setFormData((prev) => {
      const isSelected = prev.selectedManagers.includes(userId)
      return {
        ...prev,
        selectedManagers: isSelected
          ? prev.selectedManagers.filter((id) => id !== userId)
          : [...prev.selectedManagers, userId],
      }
    })
  }

  const validateForm = () => {
    if (!formData.title?.trim()) {
      message.error('Task 명을 입력해주세요')
      return false
    }
    if (!formData.endDate) {
      message.error('마감 일정을 선택해주세요')
      return false
    }
    if (!formData.hour || !formData.minute) {
      message.error('완료 시간을 선택해주세요')
      return false
    }
    if (formData.selectedManagers.length === 0) {
      message.error('담당자를 선택해주세요')
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    const endDateTime = dayjs(formData.endDate)
      .hour(parseInt(formData.hour))
      .minute(parseInt(formData.minute))
      .second(0)
      .format('YYYY-MM-DD HH:mm:ss')

    // 서브태스크 데이터 변환
    const subTasks = formData.subTasks.map((subTask) => {
      if (isEditMode) {
        // 수정 모드: 새로 추가한 서브태스크 (id가 'new-' 또는 'existing-'로 시작하는 경우)
        const isNewTask =
          typeof subTask.id === 'string' &&
          (subTask.id.startsWith('new-') || subTask.id.startsWith('existing-'))

        return {
          id: isNewTask ? null : subTask.id, // 새로 추가한 것은 null, 기존 것은 id 유지
          title: subTask.title,
          isDone: subTask.isDone || false,
        }
      } else {
        // 생성 모드: 모든 서브태스크가 새로 생성되므로 id 없이 title과 isDone만
        return {
          title: subTask.title,
          isDone: subTask.isDone || false,
        }
      }
    })

    const payload = {
      watchdogId: watchdogId,
      title: formData.title.trim(),
      endDateTime: endDateTime,
      memo: formData.memo?.trim() || '',
      managerIds: formData.selectedManagers,
      subTasks: subTasks,
    }

    setLoading(true)
    try {
      if (isEditMode) {
        await updateWatchdogTask(taskId, payload)
        message.success('Task가 수정되었습니다.')
      } else {
        await createWatchdogTask(payload)
        message.success('Task가 생성되었습니다.')
      }
      onClose?.()
      onSuccess?.()
    } catch (error) {
      console.log(error)
      message.error(
        isEditMode ? 'Task 수정에 실패했습니다.' : 'Task 생성에 실패했습니다.',
      )
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!taskId) return

    setLoading(true)
    try {
      await deleteWatchdogTask(taskId)
      message.success('Task가 삭제되었습니다.')
      onClose?.()
      onSuccess?.()
    } catch (error) {
      console.log(error)
      message.error('Task 삭제에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const disabledEndDate = (current) => {
    if (!current) return false
    return current < dayjs().startOf('day')
  }

  return (
    <AopsCommonModalLayout
      title={
        isEditMode ? 'Weekly Goal 하위 Task 수정' : 'Weekly Goal 하위 Task 생성'
      }
      open={open}
      onClose={onClose}
      zIndex={2000}
    >
      <div className="form-container">
        <div className="form-wrapper">
          <div className="form-section">
            <label className="form-label">
              Task 명 <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ width: '100%' }}>
              <Input
                placeholder="업무명을 입력해주세요"
                value={formData.title}
                onChange={(e) => {
                  if (e.target.value.length > 50) return
                  setFormData({ ...formData, title: e.target.value })
                }}
                className="form-input"
              />
              <div className="example-text-group">
                <Text className="example-text">Task 업무란?</Text>
                <Text
                  className="example-text"
                  style={{
                    fontSize: '12px',
                    color: '#585F65',
                    fontWeight: '400',
                  }}
                >
                  상위 업무를 완료하기 위해 담당자가 작업해야 하는 단위 작업
                </Text>
              </div>
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">
              마감 일정 <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ width: '100%' }}>
              <DatePicker
                placeholder="날짜를 선택해주세요"
                value={formData.endDate}
                onChange={(date) =>
                  setFormData((prev) => ({ ...prev, endDate: date }))
                }
                format="YYYY-MM-DD"
                suffixIcon={<CalendarFilled />}
                disabledDate={disabledEndDate}
                style={{
                  width: '50%',
                  height: '48px',
                }}
                className="form-input ant-picker"
                readOnly={true}
              />
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">
              완료 시간 <span style={{ color: 'red' }}>*</span>
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                // backgroundColor: 'red',
                // height: '50px',
                overflow: 'visible',
              }}
            >
              <Select
                className=" ant-select-selector  "
                style={{
                  width: 80,
                  height: '46px',
                  borderColor: '#E2EAEF !important',
                }}
                value={formData.hour}
                onChange={(hour) => setFormData({ ...formData, hour })}
                options={hours.map((hour) => ({ label: hour, value: hour }))}
                popupMatchSelectWidth={false}
                placeholder="Ex) 09"
              />
              <span style={{ color: '#383E44', fontWeight: 700 }}>:</span>
              <Select
                className="aops-ant-select-selector "
                style={{
                  width: 80,
                  height: '46px',
                  borderColor: '#E2EAEF !important',
                }}
                value={formData.minute}
                onChange={(minute) => setFormData({ ...formData, minute })}
                options={minutes.map((minute) => ({
                  label: minute,
                  value: minute,
                }))}
                popupMatchSelectWidth={false}
                placeholder="Ex) 30"
              />
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">
              담당자 <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {teamUsers.map((user) => {
                  const isSelected = formData.selectedManagers.includes(user.id)
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => toggleManager(user.id)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '100px',
                        border: `1px solid ${isSelected ? '#383E44' : '#E2EAEF'}`,
                        background: isSelected ? '#383E44' : '#FFFFFF',
                        color: isSelected ? '#FFFFFF' : '#191A1C',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user.name}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* 하위업무 */}
          <div
            className="form-section"
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <label className="form-label" style={{ marginBottom: '12px' }}>
              하위업무
            </label>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  border: '1px solid #E2EAEF',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* 헤더 */}
                <div
                  style={{
                    background: '#F6F7FA',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2EAEF',
                  }}
                >
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#383E44',
                    }}
                  >
                    하위업무 생성하기
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#8A949E',
                      marginTop: '2px',
                    }}
                  >
                    이 테스크에서 체크해야 할 업무를 적어보세요
                  </div>
                </div>

                {/* 그리드 영역 */}
                <div
                  style={{
                    background: '#FFFFFF',
                    padding: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      columnGap: '40px',
                      rowGap: '12px',
                      width: '100%',
                      paddingRight: '15px',
                    }}
                  >
                    {/* 저장된 항목 리스트 */}
                    {formData.subTasks.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 12px',
                            background: '#FFFFFF',
                            borderRadius: '8px',
                            border: `1px solid ${item.isDone ? '#E2EAEF' : '#383E44'}`,
                            height: '44px',
                            minWidth: 0,
                          }}
                        >
                          <Checkbox
                            checked={item.isDone}
                            onChange={() => toggleCheckItem(item.id)}
                            className="custom-circle-checkbox"
                            style={{ marginRight: '10px' }}
                          />
                          {editingId === item.id ? (
                            <Input
                              value={item.title}
                              variant="borderless"
                              autoFocus
                              onBlur={() => setEditingId(null)}
                              onPressEnter={() => setEditingId(null)}
                              onChange={(e) =>
                                handleTitleChange(item.id, e.target.value)
                              }
                              style={{
                                padding: 0,
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                color: 'inherit',
                              }}
                            />
                          ) : (
                            <div
                              onClick={() => setEditingId(item.id)}
                              style={{
                                flex: 1,
                                fontSize: '14px',
                                fontWeight: '600',
                                color: item.isDone ? '#007AFF' : '#191a1c',
                                textDecoration: item.isDone
                                  ? 'line-through'
                                  : 'none',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {item.title}
                            </div>
                          )}
                        </div>
                        <div
                          onClick={() => removeSubTask(item.id)}
                          style={{
                            flexShrink: 0,
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#E2EAEF',
                            borderRadius: '50%',
                            cursor: 'pointer',
                          }}
                        >
                          <span
                            style={{
                              color: '#8A949E',
                              fontSize: '14px',
                              fontWeight: 'bold',
                            }}
                          >
                            ×
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* 현재 입력 중인 세트 */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 12px',
                          background: '#FFFFFF',
                          borderRadius: '8px',
                          border: '1px solid #E2EAEF',
                          height: '44px',
                        }}
                      >
                        <Input
                          placeholder="하위업무를 적어주세요"
                          value={tempSubTask}
                          variant="borderless"
                          style={{ flex: 1, padding: 0, minWidth: 0 }}
                          onChange={(e) => setTempSubTask(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              // e.nativeEvent.isComposing이 true이면 한글 조합 중이므로 실행하지 않음
                              if (e.nativeEvent.isComposing) return

                              handleInputSubmit()
                            }
                          }}
                        />
                      </div>
                      <button
                        onClick={handleInputSubmit}
                        disabled={!tempSubTask.trim()}
                        style={{
                          width: '52px',
                          height: '44px',
                          background: '#FFFFFF',
                          border: `1px solid ${tempSubTask.trim() ? '#007AFF' : '#D1D5DB'}`,
                          color: tempSubTask.trim() ? '#007AFF' : '#D1D5DB',
                          borderRadius: '8px',
                          cursor: tempSubTask.trim()
                            ? 'pointer'
                            : 'not-allowed',
                          fontWeight: '600',
                        }}
                      >
                        입력
                      </button>
                    </div>

                    {/* 하위 업무 추가 버튼 */}
                    {/* <button
                      onClick={() => setIsInputActive(true)}
                      disabled={isInputActive}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#FFFFFF',
                        border: `1px solid ${!isInputActive ? '#007AFF' : '#D1D5DB'}`,
                        color: !isInputActive ? '#007AFF' : '#D1D5DB',
                        borderRadius: '8px',
                        height: '44px',
                        cursor: !isInputActive ? 'pointer' : 'default',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                      }}
                    >
                      하위 업무 추가 +
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="form-section"
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <label className="form-label" style={{ marginBottom: '12px' }}>
              메모
            </label>
            <div className="textarea-with-counter" style={{ width: '100%' }}>
              <Input.TextArea
                placeholder="업무 관련한 간단한 메모를 적어주세요"
                value={formData.memo}
                onChange={(e) => {
                  if (e.target.value.length > 50) return
                  setFormData({ ...formData, memo: e.target.value })
                }}
                style={{
                  height: '80px',
                  resize: 'none',
                  borderRadius: '8px',
                  border: '1px solid #e2eaef',
                  padding: '12px 16px',
                }}
              />
              <span className="char-counter">{formData.memo.length}/50자</span>
            </div>
          </div>
        </div>

        <div className="popup-footer">
          <button
            className="btn-cancel"
            onClick={isEditMode ? handleDelete : onClose}
            disabled={loading}
          >
            {isEditMode ? '삭제' : '취소'}
          </button>
          <button
            className="btn-submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEditMode ? '수정' : '등록'}
          </button>
        </div>
      </div>
    </AopsCommonModalLayout>
  )
}

export default StandaloneWatchdogTaskPopup
